/**
 * Phone country codes utility using libphonenumber-js
 * Provides exhaustive list of country calling codes with search support
 */

import { getCountries, getCountryCallingCode, CountryCode } from 'libphonenumber-js';

// Country name mapping for display
const COUNTRY_NAMES: Record<string, string> = {
    'PT': 'Portugal', 'BR': 'Brazil', 'US': 'United States', 'GB': 'United Kingdom',
    'ES': 'Spain', 'FR': 'France', 'DE': 'Germany', 'IT': 'Italy',
    'NL': 'Netherlands', 'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria',
    'PL': 'Poland', 'SE': 'Sweden', 'NO': 'Norway', 'DK': 'Denmark',
    'FI': 'Finland', 'IE': 'Ireland', 'LU': 'Luxembourg', 'GR': 'Greece',
    'CZ': 'Czech Republic', 'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria',
    'HR': 'Croatia', 'SI': 'Slovenia', 'SK': 'Slovakia', 'EE': 'Estonia',
    'LV': 'Latvia', 'LT': 'Lithuania', 'AO': 'Angola', 'MZ': 'Mozambique',
    'CV': 'Cape Verde', 'GW': 'Guinea-Bissau', 'ST': 'São Tomé and Príncipe',
    'TL': 'Timor-Leste', 'CN': 'China', 'JP': 'Japan', 'KR': 'South Korea',
    'IN': 'India', 'AU': 'Australia', 'NZ': 'New Zealand', 'ZA': 'South Africa',
    'MX': 'Mexico', 'AR': 'Argentina', 'CL': 'Chile', 'CO': 'Colombia',
    'VE': 'Venezuela', 'PE': 'Peru', 'AE': 'United Arab Emirates', 'SA': 'Saudi Arabia',
    'IL': 'Israel', 'TR': 'Turkey', 'RU': 'Russia', 'UA': 'Ukraine',
    'CA': 'Canada', 'AF': 'Afghanistan', 'AL': 'Albania', 'DZ': 'Algeria',
    'AD': 'Andorra', 'AM': 'Armenia', 'AZ': 'Azerbaijan', 'BH': 'Bahrain',
    'BD': 'Bangladesh', 'BY': 'Belarus', 'BA': 'Bosnia and Herzegovina',
    'BW': 'Botswana', 'BN': 'Brunei', 'CY': 'Cyprus', 'EG': 'Egypt',
    'ET': 'Ethiopia', 'GE': 'Georgia', 'GH': 'Ghana', 'HK': 'Hong Kong',
    'ID': 'Indonesia', 'IQ': 'Iraq', 'IR': 'Iran', 'JO': 'Jordan',
    'KZ': 'Kazakhstan', 'KE': 'Kenya', 'KW': 'Kuwait', 'LB': 'Lebanon',
    'LY': 'Libya', 'MY': 'Malaysia', 'MT': 'Malta', 'MA': 'Morocco',
    'NG': 'Nigeria', 'PK': 'Pakistan', 'PH': 'Philippines', 'QA': 'Qatar',
    'RS': 'Serbia', 'SG': 'Singapore', 'TH': 'Thailand', 'TN': 'Tunisia',
    'VN': 'Vietnam', 'ZM': 'Zambia', 'ZW': 'Zimbabwe',
};

// Country flag emoji from country code
function getCountryFlag(countryCode: string): string {
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
}

export interface PhoneCountryCode {
    value: string;      // The dial code with + prefix, e.g., "+351"
    label: string;      // Display label: "🇵🇹 +351"
    searchText: string; // Searchable text: "PT Portugal +351"
    countryCode: string; // ISO country code: "PT"
}

// Priority countries (shown first)
const PRIORITY_COUNTRIES: CountryCode[] = ['PT', 'BR', 'US', 'GB', 'ES', 'FR', 'DE', 'IT'];

/**
 * Get all phone country codes with search-friendly metadata
 */
export function getPhoneCountryCodes(): PhoneCountryCode[] {
    const allCountries = getCountries();

    const codes: PhoneCountryCode[] = allCountries.map(countryCode => {
        const callingCode = getCountryCallingCode(countryCode);
        const flag = getCountryFlag(countryCode);
        const countryName = COUNTRY_NAMES[countryCode] || countryCode;

        return {
            value: `+${callingCode}`,
            label: `${flag} +${callingCode}`,
            searchText: `${countryCode} ${countryName} +${callingCode}`.toLowerCase(),
            countryCode,
        };
    });

    // Sort: priority countries first, then alphabetically by country code
    codes.sort((a, b) => {
        const aPriority = PRIORITY_COUNTRIES.indexOf(a.countryCode as CountryCode);
        const bPriority = PRIORITY_COUNTRIES.indexOf(b.countryCode as CountryCode);

        if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
        if (aPriority !== -1) return -1;
        if (bPriority !== -1) return 1;
        return a.countryCode.localeCompare(b.countryCode);
    });

    // Remove duplicates (some countries share calling codes)
    const seen = new Set<string>();
    return codes.filter(code => {
        const key = `${code.countryCode}-${code.value}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

// Cached result
let cachedCodes: PhoneCountryCode[] | null = null;

export function usePhoneCountryCodes(): PhoneCountryCode[] {
    if (!cachedCodes) {
        cachedCodes = getPhoneCountryCodes();
    }
    return cachedCodes;
}
